// @ts-check

/**
 * @file Renders the user search route.
 */

import {useLocalSearchParams} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../src/components/button"
import Card from "../../src/components/card"
import LabeledTextInput from "../../src/components/labeled-text-input"
import Pagination from "../../src/components/pagination"
import ScreenTitle from "../../src/components/screen-title"
import Text from "@kaspernj/api-maker/build/utils/text"
import {User} from "../../src/frontend-models/user"
import {optionalRouteParam} from "../../src/shared/route-params"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const USERS_SEARCH_PAGE_SIZE = 20

/** @typedef {{kind: "error" | "success", text: string} | null} UsersSearchStatus */
/** @typedef {(...args: unknown[]) => unknown} UsersSearchCallback */

/**
 * Resolves a callback path from a source window.
 * @param {string | undefined} callbackPath Dot-separated callback path.
 * @param {Window | null | undefined} source Window to search.
 * @returns {UsersSearchCallback | null} Bound callback or null.
 */
function callbackFromSource(callbackPath, source) {
  if (!callbackPath || !source) {
    return null
  }
  const parts = callbackPath.split(".")
  /** @type {unknown} */
  let current = source
  let parent = current
  for (const part of parts) {
    parent = current
    if (
      (typeof current !== "object" && typeof current !== "function") ||
      current === null ||
      !(part in current)
    ) {
      return null
    }
    current = Reflect.get(current, part)
  }
  if (typeof current === "function") {
    return current.bind(
      typeof parent === "object" || typeof parent === "function"
        ? parent
        : undefined
    )
  }
  return null
}

/**
 * Resolves the configured user selection callback.
 * @param {string | undefined} callbackPath Dot-separated callback path.
 * @returns {UsersSearchCallback | null} Bound selection callback or null.
 */
function resolveSelectionCallback(callbackPath) {
  if (typeof window === "undefined") {
    return null
  }
  return (
    callbackFromSource(callbackPath, window) ||
    callbackFromSource(callbackPath, window.opener)
  )
}

/** @typedef {{[key: string]: unknown}} UsersSearchScreenProps */

/**
 * @typedef {object} UsersSearchScreenState
 * @property {{email: string, firstName: string, lastName: string}} filters Current search filters.
 * @property {boolean} loading Whether users are loading.
 * @property {number} page Current result page.
 * @property {UsersSearchStatus} status Screen status message.
 * @property {number} totalCount Number of matching users.
 * @property {User[]} users Loaded users.
 */

/**
 * User search route migrated from the legacy Rails/React app.
 * @augments {ShapeComponent<UsersSearchScreenProps, UsersSearchScreenState>}
 */
class UsersSearchScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {string | undefined} */
  selectFunction

  /** @type {UsersSearchScreenState} */
  state = {
    filters: {
      email: "",
      firstName: "",
      lastName: ""
    },
    loading: true,
    page: 1,
    status: null,
    totalCount: 0,
    users: []
  }

  /** @type {ReturnType<typeof setTimeout> | null} */
  searchTimer = null
  searchRequestId = 0

  /** Initializes route params and scheduled search loading. */
  setup() {
    useLocale()
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()

    this.params = params
    this.selectFunction = optionalRouteParam(params, "jscallback", "JS callback") ?? undefined
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const email = this.s.filters.email
    const firstName = this.s.filters.firstName
    const lastName = this.s.filters.lastName
    const page = this.s.page
    useEffect(() => {
      this.scheduleSearch()
      return () => {
        if (this.searchTimer) clearTimeout(this.searchTimer)
      }
    }, [email, firstName, lastName, page])
  }

  /**
   * Renders the user search screen.
   * @returns {ReactNode} User search content.
   */
  render() {
    const {filters, loading, page, status, totalCount, users} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const resultsText = users
      .map((user) => `${user.name()} ${user.email()}`)
      .join("\n")
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="usersSearchScreen"
      >
        <ScreenTitle>{_("User search")}</ScreenTitle>
        <Text
          style={styles.title ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="usersSearchScreen/title"
        >
          {_("User search")}
        </Text>
        <Card
          style={styles.filterPanel ||= {
            gap: 12,
            marginBottom: 16
          }}
          testID="usersSearchFilters"
        >
          <LabeledTextInput
            label={_("First name")}
            onChangeText={this.tt.onFirstNameChangeText}
            testID="usersSearchFilters/firstNameInput"
            value={filters.firstName}
          />
          <LabeledTextInput
            label={_("Last name")}
            onChangeText={this.tt.onLastNameChangeText}
            testID="usersSearchFilters/lastNameInput"
            value={filters.lastName}
          />
          <LabeledTextInput
            label={_("Email")}
            onChangeText={this.tt.onEmailChangeText}
            testID="usersSearchFilters/emailInput"
            value={filters.email}
          />
        </Card>
        {status &&
          <Text
            style={styles[`status-${themeName}-${status.kind}`] ||= {
              color: status.kind === "error" ? colors.dangerText : colors.successText,
              marginBottom: 16
            }}
            testID="usersSearchStatusMessage"
          >
            {status.text}
          </Text>
        }
        <Card
          style={styles.resultsPanel ||= {
            flex: 1
          }}
          testID="usersSearchResults"
        >
          <Text
            style={styles.resultsTitle ||= {
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="usersSearchResults/title"
          >
            {_("Results")}
          </Text>
          {loading &&
            <Text
              style={styles[`loadingText-${themeName}`] ||= {
                color: colors.textMuted
              }}
              testID="usersSearchResults/loadingText"
            >
              {_("Loading users...")}
            </Text>
          }
          {!loading && users.length === 0 &&
            <Text
              style={styles[`emptyText-${themeName}`] ||= {
                color: colors.textMuted
              }}
              testID="usersSearchResults/emptyText"
            >
              {_("No users match the current filters")}
            </Text>
          }
          <View
            style={styles.resultsList ||= {
              gap: 12
            }}
            testID="usersSearchResults/list"
          >
            <Text
              style={styles.hiddenResultsText ||= {
                color: "transparent",
                fontSize: 1,
                height: 1,
                lineHeight: 1,
                overflow: "hidden",
                pointerEvents: "none",
                width: 1
              }}
              testID="usersSearchResults/listText"
            >
              {resultsText}
            </Text>
            {users.map((user) => {
              const userId = user.id()
              return (
                <View
                  key={userId}
                  style={styles[`resultCard-${themeName}`] ||= {
                    backgroundColor: colors.surfaceMuted,
                    borderColor: colors.border,
                    borderRadius: 10,
                    borderWidth: 1,
                    gap: 4,
                    padding: 14
                  }}
                  testID={`usersSearchResults/user-${userId}`}
                >
                  <Text
                    style={styles.resultName ||= {
                      fontSize: 16,
                      fontWeight: 700
                    }}
                    testID={`usersSearchResults/user-${userId}/name`}
                  >
                    {user.name()}
                  </Text>
                  <Text
                    style={styles[`resultEmail-${themeName}`] ||= {
                      color: colors.textMuted,
                      fontSize: 14,
                      marginBottom: 8
                    }}
                    testID={`usersSearchResults/user-${userId}/email`}
                  >
                    {user.email()}
                  </Text>
                  <View
                    style={styles.selectAction ||= {alignSelf: "flex-start"}}
                  >
                    <Button
                      iconName="circle-check"
                      label={_("Select")}
                      onPress={
                        this.selectUserHandlers[userId] ||= () =>
                          this.onSelectPress(user)
                      }
                      testID={`usersSearchResults/user-${userId}/selectButton`}
                    />
                  </View>
                </View>
              )
            })}
          </View>
          {!loading &&
            <Pagination
              onPageChange={this.tt.onPageChange}
              page={page}
              pageSize={USERS_SEARCH_PAGE_SIZE}
              testID="usersSearchResults/pagination"
              totalCount={totalCount}
            />
          }
        </Card>
      </View>
    )
  }
  selectUserHandlers = /** @type {Record<number, () => void>} */ {}

  /** Schedules a debounced user search. */
  scheduleSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      void this.runSearch()
    }, 250)
  }

  /**
   * Searches users with the current filters.
   * @returns {Promise<void>} Resolves when search state finishes updating.
   */
  runSearch = async () => {
    const requestId = ++this.searchRequestId
    const {filters, page} = this.s
    this.s.loading = true
    try {
      /** @type {Record<string, unknown>} */
      const ransackParams = {}
      if (filters.firstName) ransackParams.firstNameCont = filters.firstName
      if (filters.lastName) ransackParams.lastNameCont = filters.lastName
      if (filters.email) ransackParams.emailCont = filters.email
      const baseQuery = User.ransack(ransackParams)
      const [users, totalCount] = await Promise.all([
        baseQuery
          .clone()
          .select(["id", "name", "firstName", "lastName", "email"])
          .order([["firstName", "asc"], ["lastName", "asc"], ["id", "asc"]])
          .page(page)
          .perPage(USERS_SEARCH_PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count()
      ])
      if (this.searchRequestId !== requestId) return
      const totalPages = Math.max(1, Math.ceil(totalCount / USERS_SEARCH_PAGE_SIZE))
      if (page > totalPages) {
        this.s.page = totalPages
        this.s.totalCount = totalCount
        return
      }
      this.s.totalCount = totalCount
      this.s.users = users
      this.s.status = null
      this.s.loading = false
    } catch (error) {
      if (this.searchRequestId !== requestId) return
      this.s.totalCount = 0
      this.s.users = []
      this.s.status = {
        kind: "error",
        text: `${_("Could not load users")}: ${error instanceof Error ? error.message : String(error)}`
      }
      this.s.loading = false
    }
  }

  /**
   * Applies one search filter value.
   * @param {"email" | "firstName" | "lastName"} key Filter key to update.
   * @param {string} value Text to store for the selected filter.
   * @returns {void} Nothing.
   */
  updateFilter(key, value) {
    this.s.filters = {
      ...this.s.filters,
      [key]: value
    }
    this.s.page = 1
  }

  /**
   * Loads the requested result page.
   * @param {number} page Page number selected in the pagination control.
   * @returns {void} Nothing.
   */
  onPageChange = (page) => {
    this.s.page = page
  }

  /**
   * Applies the first name filter.
   * @param {string} value Text to search in first names.
   * @returns {void} Nothing.
   */
  onFirstNameChangeText = (value) => {
    this.tt.updateFilter("firstName", value)
  }

  /**
   * Applies the last name filter.
   * @param {string} value Text to search in last names.
   * @returns {void} Nothing.
   */
  onLastNameChangeText = (value) => {
    this.tt.updateFilter("lastName", value)
  }

  /**
   * Applies the email filter.
   * @param {string} value Text to search in emails.
   * @returns {void} Nothing.
   */
  onEmailChangeText = (value) => {
    this.tt.updateFilter("email", value)
  }

  /**
   * Sends a selected user to the callback.
   * @param {User} user User to select.
   * @returns {void} Nothing.
   */
  onSelectPress(user) {
    const callback = resolveSelectionCallback(this.tt.selectFunction)
    if (!callback) {
      this.s.status = {
        kind: "error",
        text: _("Could not find the selection callback")
      }
      return
    }
    callback(user.id())
    this.s.status = {
      kind: "success",
      text: _("User selected")
    }
  }
}

/** @type {NamedExoticComponent<UsersSearchScreenProps>} */
const UsersSearchScreenComponent = memo(shapeComponent(UsersSearchScreen))

export default UsersSearchScreenComponent
